import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, ShieldCheck, Minus, Plus, Loader2 } from "lucide-react";
import logo from '../../assets/images/cup-artie.png';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useOnboarding } from "@/context/OnboardingContext";
import { toast } from "sonner";

export default function OnboardingV2Page2() {
    const navigate = useNavigate();
    const { data, updateData } = useOnboarding();

    useEffect(() => {
        if (!data.scriptFile && !data.scriptId) {
            navigate('/onboarding-v2');
        }
    }, [data.scriptFile, data.scriptId, navigate]);

    const [projectTitle, setProjectTitle] = useState(data.projectTitle || "");
    const [projectType, setProjectType] = useState(data.projectType || "");
    const [budget, setBudget] = useState(data.budget || "");
    const [logline, setLogline] = useState(data.logline || "");
    const [episodeCount, setEpisodeCount] = useState<number | "">(data.episodeCount || 1);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        console.log("Page 2 Data Context:", data);
        // Redirect to start if no script file or ID is present (e.g. after refresh)
        if (!data.scriptFile && !data.scriptId) {
            navigate('/onboarding-v2');
            return;
        }

        // Sync local state with context if context updates (e.g. back navigation)
        setProjectTitle(data.projectTitle);
        setProjectType(data.projectType);
        setBudget(data.budget);
        setLogline(data.logline);
        setEpisodeCount(data.episodeCount);
    }, [data, navigate]);

    const parseBudget = (str: string, projectType: string = "") => {
        if (!str || str === "I'm not sure") {
            // Return reasonable defaults based on project type when user is unsure
            switch (projectType) {
                case "Vertical short":
                    return 35000;  // Midpoint of $20K-$50K range
                case "Long-form episodic":
                    return 175000; // Midpoint of $50K-$300K range
                case "Short film":
                    return 175000; // Midpoint of $50K-$300K range
                case "Feature film":
                case "Other":
                default:
                    return 500000; // Midpoint of $300K-$700K range
            }
        }

        const matches = str.match(/(\d+(?:\.\d+)?)\s*[kKmM]?/g);
        if (!matches) return 0;

        let maxVal = 0;
        matches.forEach(m => {
            const num = parseFloat(m);
            if (isNaN(num)) return;

            let multiplier = 1;
            const lower = m.toLowerCase();
            if (lower.includes('k')) multiplier = 1000;
            if (lower.includes('m')) multiplier = 1000000;

            const val = num * multiplier;
            if (val > maxVal) maxVal = val;
        });
        return maxVal;
    };

    // Removed API fetching since we are building state locally first
    // useEffect(() => { ... }, [data.projectId]);

    const handleNext = () => {
        if (!projectTitle.trim()) {
            toast.error("Please enter a project title");
            return;
        }
        if (!projectType) {
            toast.error("Please select a project type");
            return;
        }
        if (!budget) {
            toast.error("Please enter a target budget");
            return;
        }

        // Just update context state, no API calls yet
        const finalEpisodeCount = typeof episodeCount === 'number' ? episodeCount : 1;
        updateData({
            projectTitle,
            projectType,
            budget,
            logline,
            episodeCount: finalEpisodeCount
        });
        navigate('/onboarding-v2/page3');
    };

    const projectTypes = [
        {
            label: "Vertical short",
            description: "Serialized. TikTok, Reels, Shorts, etc."
        },
        {
            label: "Long-form episodic",
            description: "Serialized. YouTube, Netflix, Disney+, Roku, etc."
        },
        {
            label: "Feature film",
            description: "Standalone, 40+ minutes. Theatrical, festivals, or streaming."
        },
        {
            label: "Short film",
            description: "Standalone, under 40 minutes. Festivals, YouTube, or streaming."
        },
        {
            label: "Other",
            description: ""
        }
    ];

    const getBudgetConfig = () => {
        switch (projectType) {
            case "Vertical short":
                return {
                    question: "What's your budget per episode?",
                    options: ["Under $20K", "$20K – $50K", "$50K+", "I'm not sure"]
                };
            case "Long-form episodic":
                return {
                    question: "What's your budget per episode?",
                    options: ["Under $50K", "$50K – $300K", "$300K – $700K", "$700K – $2M", "$2M+", "I'm not sure"]
                };
            case "Short film":
                return {
                    question: "What's your project budget?",
                    options: ["Under $50K", "$50K – $300K", "$300K – $700K", "$700K+", "I'm not sure"]
                };
            case "Feature film":
            case "Other":
            default:
                return {
                    question: "What's your project budget?",
                    options: ["Under $300K", "$300K – $700K", "$700K – $2M", "$2M – $5M", "$5M+", "I'm not sure"]
                };
        }
    };

    const budgetConfig = getBudgetConfig();

    return (
        <div className="min-h-screen selection:bg-[oklch(0.70_0.18_55)] selection:text-[oklch(1_0_0)] relative bg-[oklch(1.0000_0_0)] text-[oklch(0.2686_0_0)] overflow-x-hidden flex flex-col items-center font-sans">


            <main className="relative z-10 w-full flex flex-col items-center min-h-screen">

                <div className="w-full max-w-3xl px-6 py-12 flex flex-col items-center flex-grow">
                    {/* Logo */}
                    <div className="mb-8 relative">
                        <div className="absolute inset-0 bg-[oklch(0.97_0.01_60)] blur-xl opacity-50"></div>
                        <img
                            src={logo}
                            alt="Artie"
                            className="w-48 h-48 object-contain relative z-10"
                        />
                    </div>

                    {/* Header Section */}
                    <div className="text-center max-w-2xl mb-10 animate-in slide-in-from-bottom-4 duration-500">
                        <h1 className="text-4xl md:text-5xl font-bold mb-6 font-cabinet tracking-tight text-[oklch(0.2686_0_0)]">
                            Tell me a bit about <br />your project
                        </h1>
                        <p className="text-lg text-[oklch(0.55_0_0)] font-ibm leading-relaxed mb-2">
                            This helps me understand what you are building and how to read your script. Keep it simple. You can refine this later.
                        </p>
                    </div>

                    {/* Main Form Container */}
                    <Card className="w-full bg-white shadow-xl border border-[oklch(0.92_0_0)] overflow-hidden animate-in slide-in-from-bottom-4 duration-500 delay-100 p-8 md:p-10">

                        {/* Privacy Disclaimer */}
                        <div className="mb-8 bg-[oklch(0.98_0_0)] rounded-lg px-4 py-3 flex items-center gap-3 border border-[oklch(0.95_0_0)]">
                            <ShieldCheck className="w-4 h-4 text-[oklch(0.55_0_0)] flex-shrink-0" />
                            <p className="text-xs text-[oklch(0.55_0_0)] leading-tight">
                                This information is used only to analyze and improve your project. You can update or change it later.
                            </p>
                        </div>


                        <div className="space-y-8">
                            {/* Project Title */}
                            <div className="space-y-2">
                                <label className="text-base font-bold text-[oklch(0.2686_0_0)] font-cabinet">
                                    Project Title <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    placeholder="Enter title..."
                                    value={projectTitle}
                                    onChange={(e) => setProjectTitle(e.target.value)}
                                    className="bg-white border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)] h-12 text-base"
                                />
                                <p className="text-xs text-[oklch(0.55_0_0)]">This can be a working title.</p>
                            </div>

                            {/* Project Type */}
                            <div className="space-y-3">
                                <label className="text-base font-bold text-[oklch(0.2686_0_0)] font-cabinet">
                                    What kind of project is this? <span className="text-red-500">*</span>
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {projectTypes.map((type) => (
                                        <button
                                            key={type.label}
                                            onClick={() => {
                                                setProjectType(type.label);
                                                setBudget("");
                                            }}
                                            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all border ${projectType === type.label
                                                ? "bg-[oklch(0.2686_0_0)] text-white border-[oklch(0.2686_0_0)]"
                                                : "bg-white text-[oklch(0.55_0_0)] border-[oklch(0.92_0_0)] hover:bg-[oklch(0.98_0_0)] hover:text-[oklch(0.2686_0_0)]"
                                                }`}
                                        >
                                            {type.label}
                                        </button>
                                    ))}
                                </div>
                                {projectType && (
                                    <p className="text-xs text-[oklch(0.55_0_0)] animate-in fade-in slide-in-from-top-1 duration-200">
                                        {projectTypes.find(t => t.label === projectType)?.description || ""}
                                    </p>
                                )}
                            </div>

                            {/* Episodic Series Counter */}
                            {(projectType === "Long-form episodic" || projectType === "Vertical short") && (
                                <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                                    <label className="text-base font-bold text-[oklch(0.2686_0_0)] font-cabinet">
                                        How many episodes are you thinking?
                                    </label>
                                    <div className="flex items-center">
                                        <div className="flex items-center border border-[oklch(0.92_0_0)] rounded-md bg-white">
                                            <button
                                                onClick={() => setEpisodeCount(Math.max(1, (typeof episodeCount === 'number' ? episodeCount : 1) - 1))}
                                                className="w-12 h-12 flex items-center justify-center text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:bg-[oklch(0.98_0_0)] transition-colors rounded-l-full border-r border-[oklch(0.92_0_0)]"
                                                disabled={(typeof episodeCount === 'number' ? episodeCount : 1) <= 1}
                                            >
                                                <Minus className="w-4 h-4" />
                                            </button>
                                            <input
                                                type="number"
                                                min={1}
                                                value={episodeCount}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (val === "") {
                                                        setEpisodeCount("");
                                                    } else {
                                                        const num = parseInt(val);
                                                        if (!isNaN(num)) {
                                                            setEpisodeCount(Math.max(1, num));
                                                        }
                                                    }
                                                }}
                                                onBlur={() => {
                                                    if (episodeCount === "" || episodeCount < 1) {
                                                        setEpisodeCount(1);
                                                    }
                                                }}
                                                className="w-16 h-12 flex items-center justify-center text-center font-bold text-lg text-[oklch(0.2686_0_0)] font-cabinet border-none focus:ring-0 outline-none p-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none bg-transparent"
                                            />
                                            <button
                                                onClick={() => setEpisodeCount((typeof episodeCount === 'number' ? episodeCount : 1) + 1)}
                                                className="w-12 h-12 flex items-center justify-center text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:bg-[oklch(0.98_0_0)] transition-colors rounded-r-full border-l border-[oklch(0.92_0_0)]"
                                            >
                                                <Plus className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Target Budget */}
                            <div className="space-y-3">
                                <label className="text-base font-bold text-[oklch(0.2686_0_0)] font-cabinet">
                                    {budgetConfig.question} <span className="text-red-500">*</span>
                                </label>
                                <div className="flex flex-wrap gap-2">
                                    {budgetConfig.options.map((opt) => (
                                        <button
                                            key={opt}
                                            onClick={() => setBudget(opt)}
                                            className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all border ${budget === opt
                                                ? "bg-[oklch(0.2686_0_0)] text-white border-[oklch(0.2686_0_0)]"
                                                : "bg-white text-[oklch(0.55_0_0)] border-[oklch(0.92_0_0)] hover:bg-[oklch(0.98_0_0)] hover:text-[oklch(0.2686_0_0)]"
                                                }`}
                                        >
                                            {opt}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs text-[oklch(0.55_0_0)]">This is directional. It helps me size opportunities, not lock anything in.</p>
                            </div>

                            {/* Logline */}
                            <div className="space-y-2">
                                <label className="text-base font-bold text-[oklch(0.2686_0_0)] font-cabinet">
                                    What is your logline?<span className="text-[oklch(0.55_0_0)] font-normal text-sm ml-1">(optional)</span>
                                </label>
                                <Textarea
                                    placeholder="One or two sentences is perfect. What is the story, tone, or idea?"
                                    value={logline}
                                    onChange={(e) => setLogline(e.target.value)}
                                    className="min-h-[120px] bg-white border-[oklch(0.92_0_0)] focus:border-[oklch(0.70_0.18_55)] focus:ring-[oklch(0.70_0.18_55)] focus-visible:ring-0 focus-visible:border-[oklch(0.70_0.18_55)] text-base resize-none"
                                />
                                <p className="text-xs text-[oklch(0.55_0_0)]">This gives me context beyond the script itself.</p>
                            </div>

                        </div>
                    </Card>

                </div>

                {/* Footer & Navigation */}
                <div className="w-full mt-auto pb-10 px-6 md:px-20">
                    <div className="w-full border-t border-[oklch(0.92_0_0)] mb-8" />
                    <div className="w-full flex items-center justify-between">
                        <div className="flex-1 flex justify-start">
                            <Button
                                variant="ghost"
                                className="text-[oklch(0.55_0_0)] hover:text-[oklch(0.2686_0_0)] hover:bg-transparent pl-0"
                                onClick={() => navigate('/onboarding-v2', { state: { keepData: true } })}
                            >
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back
                            </Button>
                        </div>

                        {/* Progress Indicators */}
                        <div className="flex gap-2 justify-center">
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.2686_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                            <div className="w-2.5 h-2.5 rounded-full bg-[oklch(0.92_0_0)]"></div>
                        </div>

                        <div className="flex-1 flex justify-end">
                            <Button
                                className="bg-black hover:bg-black/90 text-white rounded-xl px-8 h-12 shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={handleNext}
                                disabled={isSaving || !projectTitle.trim() || !projectType || !budget}
                            >
                                {isSaving ? (
                                    <>
                                        Saving... <Loader2 className="ml-2 h-4 w-4 animate-spin" />
                                    </>
                                ) : (
                                    <>
                                        Continue
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>

            </main>
        </div>
    );
}